import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import "@/ticketing.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, ArrowLeft, Ticket, CheckCircle2, Users, Printer } from "lucide-react";
import { useTranslation } from "react-i18next";
import DKFooter from "@/components/DKFooter";
import { useBranding } from "@/hooks/useBranding";
import LanguageThemeSelector from "@/components/LanguageThemeSelector";

const FALLBACK_SERVICES = [
  { key: "Accueil", label: "Accueil général", description: "Renseignement, orientation", prefix: "A", tone: "from-blue-500 to-blue-700", enabled: true },
  { key: "Réclamations", label: "Réclamations", description: "Dépôt et suivi de réclamations", prefix: "R", tone: "from-orange-500 to-red-600", enabled: true },
  { key: "Cotisations", label: "Cotisations", description: "Paiements, échéanciers", prefix: "C", tone: "from-emerald-500 to-teal-700", enabled: true },
  { key: "Prestations", label: "Prestations", description: "Demandes et remboursements", prefix: "P", tone: "from-violet-500 to-purple-700", enabled: true },
];

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(i);
  }, []);
  return now;
}

function interpolate(tpl, vars) {
  if (!tpl) return "";
  return tpl.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);
}

function StepChoose({ onPick, now, services, messages, company, locale }) {
  const { t } = useTranslation();
  return (
    <div className="animate-in-up">
      <div className="text-center mb-10">
        <div className="text-[11px] uppercase tracking-[0.32em] text-blue-200 mb-3">{t("borne.header")}</div>
        <h1 className="font-display text-5xl md:text-6xl xl:text-7xl font-bold text-white leading-[1] tracking-tight" data-testid="borne-welcome-title">
          {interpolate(messages?.welcome_title, { company })}
        </h1>
        <p className="text-blue-100/85 mt-4 text-lg md:text-xl">{messages?.welcome_subtitle}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 max-w-5xl mx-auto">
        {services.map((s) => (
          <button
            key={s.key}
            onClick={() => onPick(s)}
            data-testid={`borne-service-${s.prefix}`}
            className="tile-service group relative bg-white/[0.06] backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-8 text-left overflow-hidden"
          >
            <div className={`absolute -top-16 -right-16 w-40 h-40 rounded-full bg-gradient-to-br ${s.tone || "from-blue-500 to-blue-700"} opacity-30 blur-2xl`} />
            <div className="relative z-10">
              <div className="text-[10px] uppercase tracking-[0.24em] text-blue-200 mb-2">{t("borne.service")}</div>
              <div className="font-display text-2xl md:text-3xl font-bold text-white leading-tight">{s.label}</div>
              {s.description && (
                <div className="text-blue-100/70 text-sm mt-2 line-clamp-2">{s.description}</div>
              )}
              <div className="mt-6 flex items-center justify-between">
                <span className="font-mono text-sm text-blue-200">{s.prefix}</span>
                <div className="w-10 h-10 rounded-full bg-white/10 grid place-items-center group-hover:bg-white/20">
                  <Ticket className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="text-center mt-10 text-blue-200/70 text-sm font-mono">
        {now.toLocaleDateString(locale === "en" ? "en-GB" : "fr-FR", { weekday: "long", year: "numeric", month: "long", day: "numeric" })} · {now.toLocaleTimeString(locale === "en" ? "en-GB" : "fr-FR")}
      </div>
    </div>
  );
}

function StepInfo({ service, onSubmit, onBack, loading }) {
  const { t } = useTranslation();
  const [nom, setNom] = useState("");
  const [tel, setTel] = useState("");
  const [motif, setMotif] = useState("");

  const submit = (e) => {
    e.preventDefault();
    onSubmit({ nom_client: nom.trim() || null, telephone: tel.trim() || null, motif: motif.trim() || null });
  };

  return (
    <div className="max-w-lg mx-auto animate-in-up">
      <button onClick={onBack} className="text-blue-200 hover:text-white mb-6 flex items-center gap-2" data-testid="borne-back">
        <ArrowLeft className="w-4 h-4" /> {t("borne.back")}
      </button>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.32em] text-blue-200 mb-2">{t("borne.service")}</div>
        <h2 className="font-display text-4xl font-bold text-white">{service.label}</h2>
        <p className="text-blue-100/80 mt-2">{t("borne.info_title")}</p>
      </div>

      <form onSubmit={submit} className="space-y-4 bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-2xl p-6" data-testid="borne-form">
        <div>
          <Label className="text-blue-100">{t("borne.name")}</Label>
          <Input value={nom} onChange={(e) => setNom(e.target.value)} data-testid="borne-nom-input" className="mt-1.5 bg-white/10 border-white/20 text-white h-12 placeholder:text-blue-200/50" />
        </div>
        <div>
          <Label className="text-blue-100">{t("borne.phone")}</Label>
          <Input value={tel} onChange={(e) => setTel(e.target.value)} placeholder="+221 ..." data-testid="borne-tel-input" className="mt-1.5 bg-white/10 border-white/20 text-white h-12 placeholder:text-blue-200/50" />
        </div>
        <div>
          <Label className="text-blue-100">Motif</Label>
          <Input value={motif} onChange={(e) => setMotif(e.target.value)} data-testid="borne-motif-input" className="mt-1.5 bg-white/10 border-white/20 text-white h-12 placeholder:text-blue-200/50" />
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-blue-500 to-violet-600 hover:from-blue-600 hover:to-violet-700"
          data-testid="borne-submit"
        >
          {loading ? "…" : (
            <span className="inline-flex items-center gap-2"><Ticket className="w-5 h-5" /> {t("borne.get_ticket")}</span>
          )}
        </Button>
      </form>
    </div>
  );
}

function StepTicket({ result, onNew, messages }) {
  const { t } = useTranslation();
  const tk = result.ticket;
  const printFn = () => window.print();
  return (
    <div className="max-w-2xl mx-auto text-center animate-in-up">
      <div className="mx-auto w-24 h-24 rounded-full bg-emerald-500/20 grid place-items-center mb-6 border border-emerald-400/30">
        <CheckCircle2 className="w-12 h-12 text-emerald-300" />
      </div>
      <div className="text-[11px] uppercase tracking-[0.32em] text-blue-200 mb-3" data-testid="borne-ticket-thanks">
        {messages?.ticket_thanks || t("borne.thanks")}
      </div>
      <div className="ticket-number-huge text-white" data-testid="borne-ticket-number">{tk.numero}</div>
      <div className="mt-4 text-blue-100 text-xl">{t("borne.service")} : <b className="text-white">{tk.service}</b></div>
      <div className="mt-8 grid grid-cols-2 gap-4 max-w-md mx-auto">
        <div className="bg-white/[0.06] border border-white/10 rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-widest text-blue-200">{t("borne.position")}</div>
          <div className="font-display text-3xl font-bold text-white">{result.position}</div>
        </div>
        <div className="bg-white/[0.06] border border-white/10 rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-widest text-blue-200">~ min</div>
          <div className="font-display text-3xl font-bold text-white">~{result.estimated_wait_min}</div>
        </div>
      </div>

      <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Button onClick={printFn} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20 h-12 px-6" data-testid="borne-print">
          <Printer className="w-4 h-4 mr-2" /> {t("borne.print")}
        </Button>
        <Button onClick={onNew} className="bg-white text-blue-900 hover:bg-blue-50 h-12 px-6" data-testid="borne-new-ticket">
          {t("borne.new_ticket")}
        </Button>
      </div>

      <p className="text-blue-200/70 text-sm mt-8">
        {messages?.ticket_hint || t("borne.hint")}
      </p>
    </div>
  );
}

export default function BorneTicket() {
  const now = useClock();
  const { branding } = useBranding();
  const { i18n } = useTranslation();
  const [step, setStep] = useState("choose"); // choose | info | result
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (info) => {
    setLoading(true);
    try {
      const { data } = await api.post("/public/tickets", { service: service.key, ...info });
      setResult(data);
      setStep("result");
    } catch (e) {
      alert("Erreur : " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setService(null);
    setResult(null);
    setStep("choose");
  };

  const queueColor = branding?.queue_color || "#1E3A8A";
  const services = (branding?.services || FALLBACK_SERVICES).filter((s) => s.enabled !== false);
  const messages = branding?.borne_messages || {
    welcome_title: "Bienvenue chez {company}",
    welcome_subtitle: "Choisissez le service pour retirer votre ticket",
    ticket_thanks: "Merci ! Votre ticket a bien été enregistré",
    ticket_hint: "Conservez votre numéro.",
    borne_header: "Borne — file d'attente",
  };
  const company = branding?.company_name || "dkconnect";

  return (
    <div
      className="min-h-screen borne-screen relative overflow-hidden flex flex-col"
      style={{
        background: `linear-gradient(135deg, ${queueColor} 0%, ${branding?.secondary_color || "#0B1B4B"} 55%, #2E1065 100%)`,
      }}
    >
      <div className="grain absolute inset-0 opacity-40" />
      <LanguageThemeSelector variant="floating" />
      <header className="relative z-10 px-6 md:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur-md grid place-items-center border border-white/15 overflow-hidden">
            {branding?.logo_data_url ? (
              <img src={branding.logo_data_url} alt="Logo" className="w-full h-full object-contain" />
            ) : (
              <Building2 className="w-6 h-6 text-white" />
            )}
          </div>
          <div>
            <div className="font-display font-bold text-xl text-white tracking-tight">{company}</div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-blue-200">{messages?.borne_header}</div>
          </div>
        </div>
        <a href="/ecran" target="_blank" rel="noreferrer" className="text-blue-200 hover:text-white text-sm inline-flex items-center gap-2" data-testid="borne-link-display">
          <Users className="w-4 h-4" /> {messages?.display_header || "Écran d'affichage"}
        </a>
      </header>

      <main className="relative z-10 px-6 md:px-10 py-8 md:py-12 flex-1">
        {step === "choose" && (
          <StepChoose
            onPick={(s) => { setService(s); setStep("info"); }}
            now={now}
            services={services}
            messages={messages}
            company={company}
            locale={i18n.language}
          />
        )}
        {step === "info" && <StepInfo service={service} onBack={() => setStep("choose")} onSubmit={submit} loading={loading} />}
        {step === "result" && <StepTicket result={result} onNew={reset} messages={messages} />}
      </main>

      <div className="relative z-10">
        <DKFooter variant="dark" />
      </div>
    </div>
  );
}
